import React, { useState } from "react";
import { AiOutlineEllipsis } from "react-icons/ai";
import {
  MdOutlineKeyboardDoubleArrowRight,
  MdOutlineKeyboardDoubleArrowLeft,
  MdOutlineKeyboardArrowLeft,
  MdOutlineKeyboardArrowRight,
} from "react-icons/md";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const [hoveredEllipsis, setHoveredEllipsis] = useState<
    "left" | "right" | null
  >(null);

  const getPageNumbers = () => {
    const pageNumbers: (number | string)[] = [];
    const maxDisplayedPages = 3;

    if (totalPages <= maxDisplayedPages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(currentPage - 2, 1);
      let endPage = Math.min(startPage + maxDisplayedPages - 1, totalPages);

      if (endPage - startPage < maxDisplayedPages - 1) {
        startPage = Math.max(endPage - maxDisplayedPages + 1, 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (startPage > 1) {
        pageNumbers.unshift("left-ellipsis");
        pageNumbers.unshift(1);
      }

      if (endPage < totalPages) {
        pageNumbers.push("right-ellipsis");
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  const handlePageChange = (page: number | string) => {
    if (typeof page === "string") return;
    if (page < 1 || page > totalPages) return;
    onPageChange(page);
  };

  const handleEllipsisClick = (type: "left" | "right") => {
    const pageNumbers = getPageNumbers();

    if (type === "left") {
      const leftEllipsisIndex = pageNumbers.indexOf("left-ellipsis");
      if (
        leftEllipsisIndex > 0 &&
        typeof pageNumbers[leftEllipsisIndex + 1] === "number"
      ) {
        const newPage = (pageNumbers[leftEllipsisIndex + 1] as number) - 1;
        onPageChange(newPage);
      }
    } else if (type === "right") {
      const rightEllipsisIndex = pageNumbers.indexOf("right-ellipsis");
      if (
        rightEllipsisIndex >= 0 &&
        rightEllipsisIndex < pageNumbers.length - 1 &&
        typeof pageNumbers[rightEllipsisIndex - 1] === "number"
      ) {
        const newPage = (pageNumbers[rightEllipsisIndex - 1] as number) + 1;
        onPageChange(newPage);
      }
    }
  };

  if (totalPages < 2) {
    return <></>;
  }
  return (
    <div className="join mt-4">
      <button
        className="join-item btn btn-sm disabled:opacity-50"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}>
        <MdOutlineKeyboardArrowLeft />
      </button>
      {getPageNumbers().map((page, index) => (
        <button
          key={index}
          className={`join-item btn btn-sm ${
            page === currentPage
              ? "btn-active bg-primary text-white hover:bg-orange-700"
              : ""
          }`}
          onClick={() =>
            typeof page === "number"
              ? handlePageChange(page)
              : handleEllipsisClick(page.includes("left") ? "left" : "right")
          }
          onMouseEnter={() =>
            typeof page === "string" &&
            setHoveredEllipsis(page.includes("left") ? "left" : "right")
          }
          onMouseLeave={() => setHoveredEllipsis(null)}>
          {typeof page === "number" ? (
            page
          ) : hoveredEllipsis === (page.includes("left") ? "left" : "right") ? (
            page.includes("left") ? (
              <MdOutlineKeyboardDoubleArrowLeft />
            ) : (
              <MdOutlineKeyboardDoubleArrowRight />
            )
          ) : (
            <AiOutlineEllipsis />
          )}
        </button>
      ))}
      <button
        className="join-item btn btn-sm disabled:opacity-50"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}>
        <MdOutlineKeyboardArrowRight />
      </button>
    </div>
  );
};

export default Pagination;
